import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { now } from 'moment';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import moment from 'moment-timezone';
import { isBrowser } from 'react-device-detect';

import TabView, { Tab } from '../../../components/TabView';
import Strings from '../../../Strings';
import CloudDocuments from '../../CloudDocuments/CloudDocuments';
import PatientDevices from './PatientDevices';
import PatientEvent from './PatientEvent';
import PatientInfo, { DASHBOARD_ID as INFO_DASHBOAD_ID } from './PatientInfo/PatientInfo';
import PatientMedications from './PatientMedications/PatientMedications';
import { actions } from './redux/actions';
import { ADD_SCHEDULE } from '../Schedules/redux/constants';
import { actions as kitActions } from '../../Kits/redux/actions';
import TaskHistory from './TaskHistory';
import { closeModal, openModalAction } from '../../../actions/modal';
import {
  DOWNLOAD_KIT_DATA_RESULT,
  DOWNLOAD_NOTES_AND_ACTIONS_RESULT,
  PATIENT_DASHBOARD_INFO_RESULT,
  PATIENT_DETAILS_RESULT,
  PATIENT_LIST_FOR_SUGGESTION,
  ADD_TIMER_TASK,
} from './redux/constants';
import {
  DATE_FORMAT_YEAR_MONTH_DAY,
  RPM_PATIENT_STATUS,
} from '../../../constants';
import DashboardFilter from '../../Dashboards/DashboardFilter';
import { Button as RefreshButton } from '../../../components/PageHeader/Button';
import { downloadFileOnly } from '../../../utils';
import MessageDetails from '../Messages/MessageDetails';
import { getProgramDisplayName, getProgramsFromPatient, hasAccessAlignmentInFlight, hasAccessEligibilityInFlight } from '../../../utils/cmsPrograms';
import { getRetroactiveAddPrograms, getTimerPrograms } from '../../../utils/worklogPrograms';
import HubDeviceEvents from '../DeviceManagement/HubDeviceEvents';
import ShipperCapsDetails from '../../ShipperCapsDetails';
import PatientDashboard, { PATIENT_DASHBOARD_ID_PREFIX, getReadingsWidgetMetadata } from './Dashboard/PatientDashboard';
import { ORG_FEATURES, PERMISSIONS, hasPermission, orgFeatureEnabled } from '../../../utils/userPermissions';
import { sendRestPasswordMessage } from '../../../actions/auth';
import DashboardEditModeButton from '../../Dashboards/DashboardEditModeButton';
import PatientDevicesHistory from './PatientDevicesHistory';
import { dexcomActions } from './redux/dexcomActions';
import { DeviceClass, DeviceTypes } from '../../../utils/deviceTypes';
import { OnboardStages } from '../../../modals/OnboardPatientWizard/helpers';
import { isDevelopment } from '../../../utils/environment';
import DropdownMenu from '../../../components/DropdownMenu/DropdownMenu';
import PatientImportData from './PatientImportData';
import ProviderVisitSummary from './ProviderVisitSummary';
import Select from '../../../components/Select';
import PatientLog from './Dashboard/PatientLog';
import { createEnrollmentButtons, createPatientButtons } from './patientActionsDefinitions';
import PatientInfoTabs from './PatientInfo/PatientInfoTabs';
import PatientSurveys from '../../Surveys/PatientSurveys';

function PatientDetails(props) {
  const { taskList, patientDetails, patientMedications, userOrgId } = props;
  const programs = React.useMemo(() => getProgramsFromPatient(patientDetails), [patientDetails]);
  const extendedOutreachStatusEnabled = orgFeatureEnabled(ORG_FEATURES.use_outreach_status);
  const preActivationWorklogsEnabled = patientDetails?.patientEnrollment?.preActivationWorklogsEnabled === true;
  const timerPrograms = React.useMemo(
    () => getTimerPrograms(programs, preActivationWorklogsEnabled),
    [programs, preActivationWorklogsEnabled],
  );
  const retroactiveAddPrograms = React.useMemo(() => getRetroactiveAddPrograms(programs), [programs]);
  const timerProgramsKey = timerPrograms.map(program => `${program.enrollmentId}:${program.status}`).join(',');
  
  const getProgramToSelect = () => {
    return programs?.find(p => p.status === RPM_PATIENT_STATUS.activated)?.enrollmentId
    || programs?.find(p => p.status === RPM_PATIENT_STATUS.enrolled)?.enrollmentId
    || null;
  };

  const patientId = props.match.params.patientId || props.userId;
  const surveysEnabled = props.role !== 'patient'
    && orgFeatureEnabled(ORG_FEATURES.surveys_enabled)
    && hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LIST);
  const [surveysRefreshKey, setSurveysRefreshKey] = useState(0);
  const [patientsRefreshTimestamp, setPatientsRefreshTimestamp] = useState(now());
  const [refreshEnable, setRefreshEnable] = useState(true);
  const [patientDevices, setPatientDevices] = useState([]);
  const [providerVisitSummary, setProviderVisitSummary] = useState();
  const [enrollmentId, setEnrollmentId] = useState(getProgramToSelect());

  useEffect(() => {
    setEnrollmentId(getProgramToSelect());
  }, [programs]);

  useEffect(() => {
    getPatientData();
    return () => props.clearPatientInfo();
  }, [patientId]);

  useEffect(() => {
    if (!hasAccessEligibilityInFlight(patientDetails) && !hasAccessAlignmentInFlight(patientDetails)) {
      return undefined;
    }
    const intervalId = window.setInterval(() => props.getPatientInfo(patientId), 5000);
    return () => window.clearInterval(intervalId);
  }, [patientDetails, patientId, props.getPatientInfo]);

  const getPatientData = () => {
    props.getPatientInfo(patientId);
    props.getPatientKitInfo(patientId);
    props.getPatientDashboardInfo(patientId).then(resp => {
      if (resp.type === PATIENT_DASHBOARD_INFO_RESULT) {
        const deviceCount = resp.response.devices;
        const pageRequest = {
          offset: 0,
          search: '',
          limit: deviceCount,
        };
        props.getDevices(pageRequest, patientId).then(response => {
          setPatientDevices(response.response?.data?.map(d => d.deviceType));
        });
      }
    });
    props.getProviderVisitSummary(patientId).then(resp => {
      setProviderVisitSummary(resp.response.enrollments);
    });
  };

  useEffect(() => {
    if (patientDetails) {
      props.getTaskList(patientDetails?.orgId || userOrgId);
    }
  }, [patientDetails]);

  // The timer is shown only while the page-level enrollment selection is a timer-capable one
  const isTimerEnrollmentSelected = timerPrograms.some(program => program.enrollmentId === enrollmentId);

  useEffect(() => {
    if (
      patientDetails &&
      isTimerEnrollmentSelected &&
      taskList?.length > 0 &&
      !props.timer_off &&
      // (props.native_role === DOCTOR) &&
      hasPermission(PERMISSIONS.CARE_MANAGEMENT_TASK_TIMER)
    ) {
      props.openTimer({
        taskList,
        patientId,
        programs: timerPrograms,
        preActivationWorklogsEnabled,
      });
      return () => props.closeTimer();
    }
  }, [taskList, timerProgramsKey, isTimerEnrollmentSelected]);

  //#region tabs
  const pathRoot = props.role === 'patient' ? '/rpm-patient' : `/cap-patients/${patientId}`;
  const deviceClass = DeviceTypes[decodeURIComponent(props.match?.params?.deviceType)]?.deviceClass;
  const tabArray = [];
  //if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DASHBOARD)) {
    tabArray.push(
      <Tab name="Dashboard" path={`${pathRoot}/dashboard`} className="patient-tab-page" key="dashboard">
        <PatientDashboard
          patientId={patientId}
          enrollmentId={enrollmentId}
          refreshTimestamp={patientsRefreshTimestamp}
          patientDevices={patientDevices}
          patientDetails={patientDetails}
          refresh={() => onRefresh()}
        />
      </Tab>,
    );
  //}

  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_LOG)) {
    tabArray.push(
      <Tab name={Strings.capPatient.notesAndActions} path={`${pathRoot}/notes-and-actions`} key="notes-and-actions">
        <PatientLog patientId={patientId} patientDetails={patientDetails} />
      </Tab>,
    );
  }

  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_INFO)) {
    tabArray.push(
      <Tab name="Info" path={`${pathRoot}/info`} key="info">
        <PatientInfoTabs
          patientId={patientId}
          enrollmentId={enrollmentId}
          patientDetails={patientDetails}
          refreshTimestamp={patientsRefreshTimestamp}
          onRefresh={() => onRefresh()}
          onRecordConsent={consentEnrollmentId => props.onEnroll(
            patientDetails,
            () => props.getPatientInfo(patientId),
            OnboardStages.enrolling,
            consentEnrollmentId,
          )}
        />
      </Tab>,
    );
  }
  if (providerVisitSummary?.length > 0) {
    tabArray.push(
      <Tab name={Strings.providerVisitSummary} path={`${pathRoot}/provider-visit-summary`} key="provider-visit-summary">
        <ProviderVisitSummary enrollments={providerVisitSummary} />
      </Tab>,
    );
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_MEDICATIONS)) {
    tabArray.push(
      <Tab name="Medications" path={`${pathRoot}/medications`} key="medications">
        <PatientMedications
          patientId={patientId}
          patientDetails={patientDetails}
          refreshTimestamp={patientsRefreshTimestamp}
        />
      </Tab>,
    );
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_READINGS)) {
    tabArray.push(
      <Tab
        name={Strings.readings}
        patientId={patientId}
        patientDetails={patientDetails}
        path={`${pathRoot}/readings`}
        key="readings"
      >
        <PatientEvent patientId={patientId} patientDetails={patientDetails} />
      </Tab>,
    );
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_TASK_HISTORY)) {
    tabArray.push(
      <Tab name="Task History" path={`${pathRoot}/history`} key="task-history">
        <TaskHistory patientId={patientId} patientDetails={patientDetails} taskList={taskList} enrollmentId={enrollmentId} />
      </Tab>,
    );
  }
  if (hasPermission(PERMISSIONS.MESSAGES_INBOX) && patientDetails?.textPhoneNo) {
    tabArray.push(
      <Tab name="Messages" path={`${pathRoot}/messages`} key="messages">
        <MessageDetails patientId={patientId} showPatientDetails={false} showHeader={false} patientDetails={patientDetails} />
      </Tab>,
    );
  }
  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DEVICES)) {
    tabArray.push(
      <Tab name="Devices" path={`${pathRoot}/devices`} key="devices">
        {deviceClass === DeviceClass.thirdParty ? (
          <HubDeviceEvents {...props} showBackButton={false} />
        ) : [DeviceClass.hub, DeviceClass.cap, DeviceClass.ble].includes(deviceClass) ? (
          <ShipperCapsDetails {...props} showBackButton={false} />
        ) : (
          <PatientDevices patientId={patientId} patientDetails={patientDetails} kits={props.kits} />
        )}
      </Tab>,
    );
  }
  if (
    hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DEVICES) &&
    hasPermission(PERMISSIONS.DEVICES_SHOW_DELETED)
  ) {
    tabArray.push(
      <Tab name="Devices History" path={`${pathRoot}/devices-history`} key="devices-history">
        {deviceClass === DeviceClass.thirdParty ? (
          <HubDeviceEvents {...props} showBackButton={false} />
        ) : [DeviceClass.hub, DeviceClass.cap, DeviceClass.ble].includes(deviceClass) ? (
          <ShipperCapsDetails {...props} showBackButton={false} />
        ) : (
          <PatientDevicesHistory patientId={patientId} patientDetails={patientDetails} kits={props.kits} deleted />
        )}
      </Tab>,
    );
  }

  if (hasPermission(PERMISSIONS.CARE_MANAGEMENT_PATIENT_DOCUMENTS)) {
    tabArray.push(
      <Tab name={Strings.documents} path={`${pathRoot}/documents`} key="cloud-documents">
        <CloudDocuments patientId={patientId} />
      </Tab>,
    );
  }
  if (surveysEnabled) {
    tabArray.push(
      <Tab name={Strings.surveys.patientTab} path={`${pathRoot}/surveys`} key="surveys">
        <PatientSurveys patientUuid={patientDetails?.masked_id} refreshKey={surveysRefreshKey} />
      </Tab>,
    );
  }
  if (patientDetails?.rawPatientImportData?.length > 0) {
    tabArray.push(
      <Tab name={Strings.importedData} path={`${pathRoot}/import-data`} key="import-data">
        <PatientImportData importData={patientDetails.rawPatientImportData} />
      </Tab>,
    );
  }

  const activeTab = props.activeTab || tabArray[0]?.props?.name;
  //#endregion

  const onDownloadBtnClickSuccessHandler = (req, filename) => {
    const fileName =
      req?.response && req?.response?.name ? req?.response?.name : filename ? filename : moment().toString();
    downloadFileOnly(req, fileName);
  };

  const downloadNotesAndActions = () => {
    const onDateSubmited = dates => {
      const request = {
        patientId,
        includeSignature: true,
        startTimestamp: moment(dates.startDate * 1000).format(),
        endTimestamp: moment(dates.endDate * 1000).format(),
      };
      props.downloadNotesAndActions(request).then(resp => {
        if (resp.type === DOWNLOAD_NOTES_AND_ACTIONS_RESULT) {
          onDownloadBtnClickSuccessHandler(
            resp,
            `Note__${patientDetails?.patientName?.replace(' ', '_')}__${moment(request.startTimestamp).format(
              DATE_FORMAT_YEAR_MONTH_DAY,
            )}_${moment(request.endTimestamp).format(DATE_FORMAT_YEAR_MONTH_DAY)}`,
          );
        }
      });
    };
    props.openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadNotesAndActions,
      submitButtonText: Strings.download,
      maxRangeAllowedMonths: 6,
    });
  };

  const downloadKitData = () => {
    const onDateSubmited = dates => {
      const request = {
        startTimestamp: dates.startDate ? moment(dates.startDate * 1000).format() : undefined,
        endTimestamp: dates.endDate ? moment(dates.endDate * 1000).format() : undefined,
      };
      props.downloadKitData(patientId, request).then(resp => {
        if (resp.type === DOWNLOAD_KIT_DATA_RESULT) {
          onDownloadBtnClickSuccessHandler(
            resp,
            `Readings__${patientDetails?.patientName?.replace(' ', '_')}__${moment(request.startTimestamp).format(
              DATE_FORMAT_YEAR_MONTH_DAY,
            )}_${moment(request.endTimestamp).format(DATE_FORMAT_YEAR_MONTH_DAY)}`,
          );
        }
      });
    };
    props.openDateRangeSelectionModal({
      actionOnDone: onDateSubmited,
      title: Strings.downloadEvents,
      submitButtonText: Strings.download,
      startDate: moment().subtract(6, 'months').startOf('day'),
      endDate: moment().endOf('day'),
    });
  };

  const resetPatientStatus = () => {
    props.openResetPatientStatusModal(patientDetails, onRefresh);
  };

  const inviteToPortal = patient => {
    const data = {
      title: <span>{Strings.formatString(Strings.invitePatientWarning, patient?.patientName)}</span>,
      caption: Strings.invitePatient,
      onConfirmAction: actions.invitePatientToPortal(patient.id, 'email'),
      onCancelAction: null,
      onSuccessNotification: Strings.invitationSuccess,
      confirmPostAction: onRefresh,
      hideCaution: true,
    };
    props.openConfirmModal(data);
  };

  const resetPassword = patient => {
    const data = {
      title: (
        <span>
          {Strings.warnigs.adminResetPassword} <b>{patient.patientName}</b>?
        </span>
      ),
      hideCaution: true,
      onConfirmAction: sendRestPasswordMessage({
        method: 'email',
        email: patient.email,
      }),
      onCancelAction: null,
      caption: 'Confirm password reset',
    };

    props.openConfirmModal(data);
  };

  const flagPatientForDexcom = patient => {
    const data = {
      title: <span>{Strings.formatString(Strings.enableDexcomIntegrationConfirm, patient?.patientName)}</span>,
      hideCaution: true,
      onConfirmAction: dexcomActions.flagPatientForDexcom(patient.id, 'new'),
      onSuccessNotification: Strings.enableDexcomIntegrationSuccess,
      onCancelAction: null,
      confirmPostAction: onRefresh,
      caption: Strings.enableDexcomIntegration,
    };

    props.openConfirmModal(data);
  };

  const flagPatientForDexcomDisable = patient => {
    const data = {
      title: <span>{Strings.formatString(Strings.disableDexcomIntegrationConfirm, patient?.patientName)}</span>,
      hideCaution: true,
      onConfirmAction: dexcomActions.flagPatientForDexcom(patient.id, 'delete'),
      onSuccessNotification: Strings.disableDexcomIntegrationSuccess,
      onCancelAction: null,
      confirmPostAction: onRefresh,
      caption: Strings.disableDexcomIntegration,
    };

    props.openConfirmModal(data);
  };

  const onRefresh = () => {
    setPatientsRefreshTimestamp(now());
    getPatientData();
    setRefreshEnable(false);
    setTimeout(() => {
      setRefreshEnable(true);
    }, 10000);
  };

  const getDefaultEnrollmentId = () => {
    const enrollments = patientDetails?.patientEnrollment?.enrollments;
    if (!enrollments) {
      return null;
    }
    const defaultProgram = patientDetails?.patientEnrollment?.defaultProgram;
    const defaultEnrollment = defaultProgram
      ? Object.entries(enrollments).find(([, value]) => value.program === defaultProgram)
      : null;
    return defaultEnrollment?.[0] || Object.keys(enrollments)[0];
  };

  const endEnrollment = context => {
    props.onEnroll(context.patient, () => props.getPatientInfo(patientId), OnboardStages.settingEndDate, context.enrollmentId);
  };

  const enrollPatient = context => {
    const patientStatus = context.enrollment?.status;
    props.onEnroll(
      context.patient,
      () => props.getPatientInfo(patientId),
      patientStatus === RPM_PATIENT_STATUS.enrolled ? OnboardStages.activating : OnboardStages.enrolling,
      context.enrollmentId,
    );
  };

  const registerPatient = context => {
    props.onEnroll(context.patient, () => props.getPatientInfo(patientId), OnboardStages.registration, context.enrollmentId);
  };

  const handleStartEnrollment = context => {
    props.startEnrollment(context.patient, onRefresh, context.enrollmentId);
  };

  const reRegisterPatient = context => {
    props.openConfirmModal({
      title: (
        <span>
          {Strings.formatString(Strings.moveToOnboardedRegisteredWarning, context.patient.patientName)}
        </span>
      ),
      onConfirmAction: actions.clearNotEnrolled(context.patient.id, context.enrollmentId),
      confirmPostAction: onRefresh,
      caption: Strings.register,
      hideCaution: true,
    });
  };

  const rescheduleTelevisit = context => {
    props.editPatient(context.patient, onRefresh, OnboardStages.rescheduleTelevisit, context.enrollmentId);
  };

  const unregisterEnrollment = context => {
    if (!context.enrollmentId) {
      return;
    }
    props.openConfirmModal({
      title: <span>{Strings.formatString(Strings.unregisterEnrollmentWarning, context.patient.patientName)}</span>,
      onConfirmAction: actions.unregisterEnrollment(context.enrollmentId),
      confirmPostAction: onRefresh,
      onSuccessNotification: Strings.formatString(Strings.enrollmentUnregistered, context.patient.patientName),
      caption: Strings.unregister,
      hideCaution: true,
    });
  };

  const changeOutreachStatus = context => {
    props.editPatient(context.patient, onRefresh, OnboardStages.changeOutreachStatus, context.enrollmentId);
  };

  const cancelEnrollment = context => {
    if (!context.enrollmentId) {
      return;
    }
    props.openCancelEnrollmentModal(context.patient, onRefresh, context.enrollmentId);
  };

  const addMedicine = context => {
    props.onAddMedicine(
      context.patient,
      onRefresh,
      context.enrollmentId,
      context.enrollment?.status === RPM_PATIENT_STATUS.activated,
    );
  };

  const moveToNotEnrolled = context => {
    props.openMoveToNotEnrolledModal(context.patient, onRefresh, context.enrollmentId);
  };

  const editEnrollment = context => {
    props.editPatient(context.patient, onRefresh, OnboardStages.editEnrollment, context.enrollmentId);
  };

  const editPatientRecord = patient => {
    props.editPatient(patient, onRefresh, OnboardStages.editPatient);
  };

  const checkEligibility = patient => {
    props.checkPatientEligibility(patient, onRefresh);
  };

  const deletePatient = patient => {
    const deleteData = { maskedId: patient.masked_id, allowBilled: true };
    const data = {
      title: (
        <span>
          {Strings.deletePatientWarning}
          : 
          <b>{patient.patientName}</b>
          ? 
          <br />
          {' '}
          {Strings.warnigs.deletePatientWarning}
        </span>
      ),
      onConfirmAction: actions.deletePatient(deleteData, patient.id),
      onCancelAction: null,
      confirmPostAction: () => {
        onRefresh();
      },
    };
    props.openConfirmModal(data);
  };

  const getActionsFromButtons = (buttonsArray, context, enrollmentLabel) => {
    return buttonsArray
      .filter(button => {
        if (typeof button.visible === 'function') {
          return button.visible(undefined, context);
        }
        if (typeof button.visible === 'boolean') {
          return button.visible;
        }
        return true;
      })
      .map(button => {
        const text = typeof button.text === 'function' ? button.text(undefined, context) : button.text;
        if (!text) {
          return null;
        }

        const disabled = (typeof button.disabled === 'function' && button.disabled(undefined, context))
          || (typeof button.disabled === 'boolean' && button.disabled);
        const highlighted = typeof button.highlighted === 'function' ? button.highlighted(undefined, context) : button.highlighted;

        return {
          label: enrollmentLabel ? `${text} (${enrollmentLabel})` : text,
          onSelect: () => button.onClick(undefined, context),
          disabled,
          tooltip: disabled ? (typeof button.disabledTooltip === 'function' ? button.disabledTooltip(undefined, context) : button.disabledTooltip) : undefined,
          highlighted,
        };
      })
      .filter(option => option !== null);
  };

  const getPatientActionOptions = () => {
    const patientButtons = createPatientButtons({
        sendSMS: patient => props.onSendMessage(patient, props.getPatientsForSuggestion, PATIENT_LIST_FOR_SUGGESTION),
        checkEligibility,
        editPatient: editPatientRecord,
        deletePatient,
        resetPassword,
        inviteToPortal,
    });

    return getActionsFromButtons(patientButtons, patientDetails);
  };

  const getEnrollmentActionOptions = () => {
    const enrollments = Object.entries(patientDetails?.patientEnrollment?.enrollments || {});
    if (enrollments.length === 0) {
      return [];
    }

    const enrollmentButtons = createEnrollmentButtons({
      extendedOutreachStatusEnabled,
      endEnrollment,
      enrollPatient,
      handleStartEnrollment,
      registerPatient,
      resetPatientStatus,
      reRegisterPatient,
      rescheduleTelevisit,
      unregisterEnrollment,
      changeOutreachStatus,
      cancelEnrollment,
      addMedicine,
      sendSMS: patient => props.onSendMessage(patient, props.getPatientsForSuggestion, PATIENT_LIST_FOR_SUGGESTION),
      checkEligibility,
      moveToNotEnrolled,
      editEnrollment,
    });

    const hasMultipleEnrollments = enrollments.length > 1;
    return enrollments.flatMap(([currentEnrollmentId, enrollment], index) => {
      const context = {
        patient: patientDetails,
        enrollment,
        enrollmentId: currentEnrollmentId,
        patientView: true,
      };
      const enrollmentLabel = getProgramDisplayName(enrollment.program, enrollment.subprogram)
        || `${Strings.capPatient.patientStatus[enrollment.status] || enrollment.status} ${Strings.enrollment}`;

      return getActionsFromButtons(enrollmentButtons, context, hasMultipleEnrollments ? enrollmentLabel : undefined);
    });
  };

  const actionsButton = () => {
    const options = [];
    const enrollmentIdForAction = getDefaultEnrollmentId();
    const patientStatus = enrollmentIdForAction ? patientDetails?.patientEnrollment?.enrollments?.[enrollmentIdForAction]?.status : patientDetails?.enrollmentStates?.[0];
    const maskedId = patientDetails?.masked_id || patientId;
    options.push({ label: Strings.addNote, onSelect: props.onAddNote });
    options.push({ label: Strings.createActionItem, onSelect: props.onAddAction });
    if (surveysEnabled && patientDetails?.masked_id) {
      options.push({
        label: Strings.surveys.send,
        onSelect: () => props.openSendSurvey({
          patient: { uuid: patientDetails.masked_id, name: patientDetails.patientName },
          onSent: () => setSurveysRefreshKey(k => k + 1),
        }),
      });
    }
    options.push({
      label: Strings.capPatient.addTask,
      onSelect: () => props.onAddTask(
        patientId,
        taskList,
        retroactiveAddPrograms,
        enrollmentId && retroactiveAddPrograms.some(p => p.enrollmentId === enrollmentId) ? enrollmentId : undefined,
        () => props.getPatientBillableTime(patientId),
      ),
      disabled: retroactiveAddPrograms.length === 0,
      tooltip: retroactiveAddPrograms.length === 0 ? Strings.capPatient.addTaskDisabledTooltip : undefined,
    });
    if (isDevelopment()) {
      options.push({
        label: Strings.enterManualVitals,
        onSelect: () => props.openManualVitalsModal({
          patientId,
          enrollments: getProgramsFromPatient(patientDetails),
          defaultEnrollmentId: enrollmentIdForAction,
        }),
      });
    }
    if (patientDetails?.email) {
      options.push({ label: Strings.emailPatient, onSelect: () => window.location = `mailto:${patientDetails?.email}` });
    }
    if (activeTab === Strings.readings && props.kits?.length) {
      options.push({ label: Strings.downloadData, onSelect: downloadKitData });
    }
    options.push({ label: Strings.downloadNotesAndActions, onSelect: downloadNotesAndActions });
    options.push({
      label: Strings.downloadMonthlySummary,
      onSelect: () => props.openMonthlySummaryModal({
        maskedId,
        patientId,
        patientName: patientDetails?.patientName,
        enrollmentId: enrollmentIdForAction,
      }),
      disabled: !maskedId || !enrollmentIdForAction,
    });

    const enrollmentActionOptions = getEnrollmentActionOptions();
    options.push(...enrollmentActionOptions);

    const patientActionOptions = getPatientActionOptions();
    options.push(...patientActionOptions);

    if (patientStatus !== RPM_PATIENT_STATUS.ended) {
      options.push({ label: Strings.addCaregiver, onSelect: () => props.addCaregiver(activeTab === 'Info' ? onRefresh : () => {}) });
    }
    if (hasPermission(PERMISSIONS.DEVICES_DEXCOM_CGM) &&
    patientDetails?.kitIds?.length > 0 &&
    patientStatus === RPM_PATIENT_STATUS.activated) {
        options.push({
          label: !patientDetails?.dexcomStatus ? Strings.enableDexcomIntegration : Strings.resetDexcomIntegration,
          onSelect: () => flagPatientForDexcom(patientDetails),
        });
    }
    if (hasPermission(PERMISSIONS.DEVICES_DEXCOM_CGM) &&
    patientDetails?.dexcomStatus &&
    patientDetails.dexcomStatus !== 'deleted' &&
    patientDetails?.kitIds?.length > 0 &&
    patientStatus === RPM_PATIENT_STATUS.activated) {
      options.push({ label: Strings.disableDexcomIntegration, onSelect: () => flagPatientForDexcomDisable(patientDetails) });
    }

    return (
      <DropdownMenu
        title={Strings.actions}
        options={options}
      />
    );
  };

  return (
    <div className="patient-page">
      <div className="patient-page-innerdiv">
        <div className="infoHeader spaceBeteween white">
          {isBrowser ? (
            <div className="rowDirection">
              <button className="back" onClick={() => history.back()} tabIndex={0}>
                {Strings.back}
              </button>
              <div className="feildBox">
                {Strings.capPatient.patientId}: <div className="feildValue">{patientDetails?.pharmacyPatientId}</div>
              </div>
              <div className="pathHr"></div>
              <div className="feildBox">
                {Strings.name}: <div className="feildValue">{patientDetails?.patientName}</div>
              </div>
              {/* <div className="pathHr"></div>
              <div className="feildBox">
                {Strings.capPatient.dob}:{' '}
                <div className="feildValue">{moment(patientDetails?.dateOfBirth).format(DATE_MONTH_DAY_YEAR)}</div>
              </div> */}
              {/* <div className="pathHr"></div>
              <div className="feildBox">
                {Strings.kitId}: <div className="feildValue">{props.kits?.map(kit => kit.id).join(', ') || 'none'}</div>
              </div> */}
            </div>
          ) : (
            <div className="rowDirection">
              <button className="back" onClick={() => history.back()} tabIndex={0}>
                {Strings.back}
              </button>
              <div className="feildValue">{patientDetails?.mrn}</div>
              <div className="pathHr"></div>
              <div className="feildValue">{patientDetails?.patientName}</div>
            </div>
          )}
          <div className="rowDirection">
            {programs?.length > 1 && activeTab !== 'Devices' && (
              <Select
                id="enrollment-select"
                placeholder={Strings.selectEnrollment}
                data={
                  programs.map(p => ({ ...p, value: p.enrollmentId, label: `${p.displayName} (${p.status})` }))
                }
                onChange={e => setEnrollmentId(e.value)}
                value={enrollmentId || '-'}
                isDisabled={!patientDetails?.patientEnrollment?.enrollments}
              />
            )}
            {actionsButton()}
            {activeTab === 'Info' && (
              <React.Fragment>
                <DashboardEditModeButton dashboardId={INFO_DASHBOAD_ID} />
                <DashboardFilter dashboardId={INFO_DASHBOAD_ID} />
              </React.Fragment>
            )}
            {activeTab === 'Dashboard' && (
              <div className="page-header">
                <div className="right">
                  <DashboardEditModeButton dashboardId={`${PATIENT_DASHBOARD_ID_PREFIX}${patientId}`} />
                  <DashboardFilter
                    dashboardId={`${PATIENT_DASHBOARD_ID_PREFIX}${patientId}`}
                    additionalData={{
                      meds: patientMedications,
                      readingsWidgetMetadata: getReadingsWidgetMetadata(patientDevices),
                      patientDetails,
                      shippingEnabled: patientDetails?.patientEnrollment?.enrollments && Object.values(patientDetails?.patientEnrollment?.enrollments)?.[0]?.shipment
                    }}
                  />
                  <RefreshButton class="refresh" disabled={!refreshEnable} onClick={onRefresh} />
                </div>
              </div>
            )}
            {activeTab === 'Devices' && props.match?.params?.type && <div className="refresh-button-placeholder"></div>}
          </div>
        </div>
      </div>
      <TabView key="tabs" isBack={false} activeTab={activeTab} routeMode className="patient-tab-view">
        {tabArray}
      </TabView>
    </div>
  );
}

PatientDetails.propTypes = {
  activeTab: PropTypes.any,
  getPatientInfo: PropTypes.func,
  openSendSurvey: PropTypes.func,
  getPatientKitInfo: PropTypes.func,
  getTaskList: PropTypes.func,
  match: PropTypes.shape({ params: PropTypes.shape({ id: PropTypes.any }) }),
  patientDetails: PropTypes.any,
  patientMedications: PropTypes.array,
  taskList: PropTypes.any,
  onAddNote: PropTypes.func,
  isPatientMedicationsLoading: PropTypes.any,
  kits: PropTypes.array,
  onEnroll: PropTypes.func,
  onAddAction: PropTypes.func,
  onAddTask: PropTypes.func,
  getPatientBillableTime: PropTypes.func,
  clearPatientInfo: PropTypes.func,
  downloadNotesAndActions: PropTypes.func,
  onSendMessage: PropTypes.func,
  getPatientsForSuggestion: PropTypes.func,
  onAddMedicine: PropTypes.func,
  checkPatientEligibility: PropTypes.func,
  openMoveToNotEnrolledModal: PropTypes.func,
  openCancelEnrollmentModal: PropTypes.func,
  openDateRangeSelectionModal: PropTypes.func,
  downloadKitData: PropTypes.func,
  openConfirmModal: PropTypes.func,
  editPatient: PropTypes.func,
  startEnrollment: PropTypes.func,
  closeTimer: PropTypes.func,
  openTimer: PropTypes.func,
  openResetPatientStatusModal: PropTypes.func,
  role: PropTypes.string,
  userId: PropTypes.any,
  timer_off: PropTypes.any,
  getDevices: PropTypes.func,
  getProviderVisitSummary: PropTypes.func,
  onNavigate: PropTypes.func,
  addCaregiver: PropTypes.func,
  openMonthlySummaryModal: PropTypes.func,
  native_role: PropTypes.string,
};

const mapStateToProps = state => {
  const { patients } = state.superUser;
  return {
    role: state.auth.role,
    native_role: state.auth.native_role,
    taskList: patients && patients?.taskList?.data,
    patientDetails: patients?.patientDetails,
    patientMedications: patients?.patientMedications?.medications,
    isPatientMedicationsLoading: patients?.isPatientMedicationsLoading,
    kits: state.entities.kits?.patientKits,
    userId: state.auth.profile?.id,
    timer_off: state.auth.profile?.timer_off,
    userOrgId: state.auth?.organization?.masked_id,
  };
};

const mapDispatchToProps = (dispatch, props) => ({
  onNavigate: path => dispatch(push(path)),
  getTaskList: orgId => dispatch(actions.getTimerTasks(orgId)),
  getPatientInfo: patientId => dispatch(actions.getPatientInfo(patientId)),
  getPatientDashboardInfo: patientId => dispatch(actions.getPatientDahsboardInfo(patientId)),
  getDevices: (request, patientId) => dispatch(actions.getPatientDevices(request, patientId)),
  clearPatientInfo: () => dispatch(actions.clearPatientInfo()),
  getPatientKitInfo: patientId => dispatch(kitActions.getPatientKitInfo(patientId)),
  getProviderVisitSummary: patientId => dispatch(actions.getProviderVisitSummary(patientId)),
  onAddTask: (patientId, taskList, programs, enrollmentId, successAction) =>
    dispatch(
      openModalAction('update-task', {
        action: actions.addTimerTask,
        successAction,
        actionType: ADD_TIMER_TASK,
        taskList,
        patientId,
        programs,
        enrollmentId,
      }),
    ),
  getPatientBillableTime: patientId => dispatch(actions.getPatientBillableTime(patientId)),
  onAddNote: () =>
    dispatch(
      openModalAction('add-note', {
        patientId: props.match.params.patientId,
      }),
    ),
  onAddAction: () =>
    dispatch(
      openModalAction('add-note', {
        patientId: props.match.params.patientId,
        actionMode: true,
      }),
    ),
  onEnroll: (patient, nextAction, stage, requestedEnrollmentId) =>
    dispatch(actions.getPatientInfo(patient.id)).then(resp => {
      const patientData = resp?.type === PATIENT_DETAILS_RESULT ? resp.response : patient;
      const enrollmentId = requestedEnrollmentId || Object.keys(patientData?.patientEnrollment?.enrollments || {})[0];
      const enrollment = patientData?.patientEnrollment?.enrollments?.[enrollmentId];
      dispatch(
        openModalAction('onboard-patient', {
          patient: patientData,
          onSuccess: nextAction,
          stage,
          selectedEnrollmentId: enrollmentId,
        })
      );
    }),
  downloadNotesAndActions: request => dispatch(actions.downloadNotesAndActions(request)),
  downloadKitData: (id, request) => dispatch(actions.downloadKitData(id, request)),
  onSendMessage: (patient, loadOptionsAction, loadOptionsActionType) =>
    dispatch(
      openModalAction(
        'send-message',
        {
          patient,
          loadRecipients: loadOptionsAction,
          loadRecipientsActionType: loadOptionsActionType,
        },
        patient.id,
      ),
    ),
  getPatientsForSuggestion: pageRequest => dispatch(actions.getPatientsForSuggestion(pageRequest)),
  onAddMedicine: (patient, onRefresh, enrollmentId, logTask) =>
    dispatch(
      openModalAction('edit-rpm-schedule', {
        patient,
        actionType: ADD_SCHEDULE,
        onSuccess: onRefresh,
        enrollmentId,
        logTask,
      }),
    ),
  addCaregiver: onSuccess =>
    dispatch(
      openModalAction('edit-caregivers', {
        patientId: props.match.params.patientId,
        onSuccess,
      }),
    ),
  openTimer: data => dispatch(openModalAction('timer', data)),
  openSendSurvey: data => dispatch(openModalAction('send-survey-modal', data)),
  closeTimer: () => dispatch(closeModal('timer')),
  openDateRangeSelectionModal: data => dispatch(openModalAction('data-range-selection-modalion', data)),
  openMonthlySummaryModal: data => dispatch(openModalAction('monthly-summary-modal', data)),
  openManualVitalsModal: data => dispatch(openModalAction('manual-vitals-modal', data)),
  openConfirmModal: data => dispatch(openModalAction('confirmation-modal', data)),
  editPatient: (patient, onSuccess, stage, selectedEnrollmentId) =>
    dispatch(
      openModalAction('onboard-patient', {
        patientAction: actions.editPatient,
        onSuccess,
        patient,
        stage,
        selectedEnrollmentId,
      }),
    ),
  startEnrollment: (patient, onSuccess, selectedEnrollmentId) =>
    dispatch(
      openModalAction('start-enrollmend-and-schedules', {
        patient,
        onSuccess,
        selectedEnrollmentId,
      }),
    ),
  openResetPatientStatusModal: (patient, onSuccess) =>
    dispatch(openModalAction('reset-enrollment-and-unattach-devices', { patient, onSuccess })),
  checkPatientEligibility: (patient, onSuccess) => dispatch(openModalAction('check-eligibility', { patient, onSuccess })),
  openMoveToNotEnrolledModal: (patient, onSuccess, selectedEnrollmentId) =>
    dispatch(openModalAction('move-to-not-enrolled', { patient, onSuccess, selectedEnrollmentId })),
  openCancelEnrollmentModal: (patient, onSuccess, selectedEnrollmentId) =>
    dispatch(openModalAction('cancel-enrollment', { patient, onSuccess, selectedEnrollmentId })),
});

export default connect(mapStateToProps, mapDispatchToProps)(PatientDetails);
